import { Button, Flex, Group, Stack, Text } from "@mantine/core";
import { IComment, IEditorMetadata, IUserDepricated } from "@mctypes/global.types";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { IconChevronDown, IconPlus, IconSend, IconX } from "@tabler/icons-react";
import { EditorState } from "lexical/LexicalEditorState";
import React, { useRef, useState } from "react";
import { Prompt } from "react-router";
import { SingleComment } from "../../../../../../components";
import RichTextEditor, {
  lexicalStateToPlainText,
  RichEditorHandle,
} from "../../../../../../components/RichTextEditorMantine/RichTextEditor";
import { setHasUnsavedCommentInRedux } from "../../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks";
import { sortArrayByDate } from "../../../../../utils";
import NewsSearch from "../../../../projectdashboard/News/NewsSearch";

type ICommentWithReactions = IComment & {
  reactions?: { user?: { _id?: string; name?: string }; date?: string | Date; emoji?: string }[];
};

const MultiCommentMantine: React.FC<{
  comments: ICommentWithReactions[];
  onAdd?: (text: string, metadata: IEditorMetadata) => void | Promise<void>;
  onCreate?: (text: string, metadata: IEditorMetadata) => void | Promise<void>;
  onUpdate?: (commentId: string, text: string, metadata: IEditorMetadata) => void | Promise<void>;
  onSave?: (commentId: string, text: string, metadata: IEditorMetadata) => void | Promise<void>;
  onDelete: (commentId: string) => void;
  loggedInUser?: IUserDepricated;
  members?: IProjectMemberApiItem[];
  allowDelete?: boolean;
  isLoading?: boolean;
  loading?: boolean;
  sortDesc?: boolean;
  showSearch?: boolean;
  noCommentsText?: string;
  visibleCommentsOnLoad?: number;
  useBrowserPrompt?: boolean;
  alwaysShowInput?: boolean;
  showCancelButton?: boolean;
  onReact?: (commentId: string, emoji: string) => void;
  currentUserId?: string;
  offsetLeft?: boolean;
}> = ({
  comments,
  onAdd,
  onCreate,
  onUpdate,
  onSave,
  onDelete,
  loggedInUser,
  members,
  allowDelete = false,
  isLoading = false,
  loading = false,
  sortDesc = true,
  showSearch = false,
  noCommentsText,
  visibleCommentsOnLoad = 5,
  useBrowserPrompt = false,
  alwaysShowInput = true,
  showCancelButton = true,
  onReact,
  currentUserId,
  offsetLeft = false,
}) => {
  const filteredComments = comments.filter((item) => item.deleted === false);
  const [addNewEditor, setAddNewEditor] = useState(false);
  const [doShowAll, setDoShowAll] = useState(false);
  const sortedComments = sortArrayByDate(filteredComments, "createdDate", sortDesc);
  const commentsToShow = doShowAll ? sortedComments : sortedComments.slice(0, visibleCommentsOnLoad);
  const hasUnsavedComment = useAppSelector((state) => state.globalReducer.hasUnsavedComment);
  const busy = isLoading || loading;
  const showInput = alwaysShowInput || addNewEditor;

  const handleAddComment = async (text: string, metadata: IEditorMetadata) => {
    const createHandler = onCreate ?? onAdd;
    if (!createHandler) return;
    await createHandler(text, metadata);
  };

  const handleSaveComment = async (commentId: string, text: string, metadata: IEditorMetadata) => {
    const saveHandler = onSave ?? onUpdate;
    if (!saveHandler) return;
    await saveHandler(commentId, text, metadata);
  };

  const onLocationChange = () => {
    return "Du har en ulagret kommentar. Er du sikker på at du vil avbryte?";
  };

  return (
    <Stack gap="xs" w="100%" className={offsetLeft ? "pl-2" : ""}>
      {alwaysShowInput !== true && addNewEditor === false && (
        <Group justify="space-between" align="center" wrap="nowrap" mb={"xs"}>
          <Button
            size="xs"
            variant="light"
            style={{ marginLeft: "-1em" }}
            onClick={() => setAddNewEditor(true)}
            aria-label="Ny kommentar"
            title="Ny kommentar"
          >
            <IconPlus size={14} />
          </Button>
          {showSearch && <NewsSearch />}
        </Group>
      )}

      {useBrowserPrompt === true && <Prompt when={hasUnsavedComment === true} message={onLocationChange} />}

      <CommentInputSection
        isVisible={showInput}
        isLoading={busy}
        showCancelButton={showCancelButton}
        members={members}
        onAddComment={handleAddComment}
        onAbort={() => setAddNewEditor(false)}
      />

      {busy && (
        <Text size="sm" c="dimmed">
          Laster kommentarer...
        </Text>
      )}

      {commentsToShow.length > 0 && (
        <Stack gap="lg">
          {commentsToShow.map((comment: ICommentWithReactions) => (
            <SingleComment
              key={comment._id}
              comment={comment}
              editEnabled={
                loggedInUser?._id === (comment.createdBy as IUserDepricated)?._id ||
                loggedInUser?._id === comment.createdBy
              }
              deleteEnabled={
                loggedInUser?._id === (comment.createdBy as IUserDepricated)?._id ||
                loggedInUser?._id === comment.createdBy ||
                allowDelete
              }
              onSave={(text, meta) => handleSaveComment(comment._id, text, meta)}
              onDelete={() => onDelete(comment._id)}
              members={members}
              reactions={comment.reactions}
              onReact={onReact ? (emoji) => onReact(comment._id, emoji) : undefined}
              currentUserId={currentUserId}
            />
          ))}

          {sortedComments.length > visibleCommentsOnLoad && doShowAll === false && (
            <Button variant="default" onClick={() => setDoShowAll(true)} rightSection={<IconChevronDown size={14} />}>
              Vis alt
            </Button>
          )}
        </Stack>
      )}

      {filteredComments.length < 1 && busy !== true && noCommentsText != null && (
        <Text ta="center" mt="sm" c="dimmed">
          {noCommentsText}
        </Text>
      )}
    </Stack>
  );
};

export default MultiCommentMantine;

export const CommentInputSection: React.FC<{
  onAddComment: (text: string, meta: IEditorMetadata) => Promise<void> | void;
  isVisible: boolean;
  isLoading: boolean;
  showCancelButton?: boolean;
  members?: IProjectMemberApiItem[];
  onAbort?: () => void;
}> = ({ onAddComment, members, isLoading, isVisible, showCancelButton = true, onAbort }) => {
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [plainText, setPlainText] = useState("");
  const [editorMetadata, setEditorMetadata] = useState<IEditorMetadata>(null);
  const [hasImages, setHasImages] = useState(false);
  const dispatch = useAppDispatch();
  const hasUnsavedComment = useAppSelector((state) => state.globalReducer.hasUnsavedComment);

  const add = async () => {
    const plainText = await lexicalStateToPlainText(editorState);

    await onAddComment(JSON.stringify(editorState), { ...editorMetadata, plainText: plainText });
    setEditorState(null);
    setEditorMetadata(null);
    setPlainText("");
    setHasImages(false);
    dispatch(setHasUnsavedCommentInRedux(false));
    onAbort?.();
  };

  const abort = () => {
    setEditorState(null);
    setEditorMetadata(null);
    setPlainText("");
    setHasImages(false);
    dispatch(setHasUnsavedCommentInRedux(false));
    onAbort?.();
  };

  const handleChange = (editorState, plainText, hasImages) => {
    setEditorState(editorState);
    setPlainText(plainText);
    setHasImages(hasImages);
    if (plainText.length > 0 && hasUnsavedComment !== true) {
      dispatch(setHasUnsavedCommentInRedux(true));
    } else if (plainText.length < 1) {
      dispatch(setHasUnsavedCommentInRedux(false));
    }
  };
  const editorRef = useRef<RichEditorHandle | null>(null);

  if (isVisible !== true) {
    return null;
  }

  return (
    <Stack gap="sm">
      <RichTextEditor
        ref={editorRef}
        disabled={isLoading}
        members={members}
        onChange={handleChange}
        handleMentions={(e) => setEditorMetadata({ ...editorMetadata, mentionIdArray: e })}
      />
      <Flex justify="flex-end" w="100%" gap="xs">
        {showCancelButton && (
          <Button
            onClick={() => {
              abort();
              editorRef.current?.reset();
            }}
            variant="default"
          >
            <IconX size={16} />
          </Button>
        )}
        <Button
          loading={isLoading}
          leftSection={<IconSend size={16} />}
          variant="filled"
          disabled={isLoading || (plainText.length < 1 && hasImages === false)}
          onClick={async () => {
            await add();
            editorRef.current?.reset();
          }}
        >
          Send
        </Button>
      </Flex>
    </Stack>
  );
};
